import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, GitBranch, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const MA_KEYWORDS = [
  'acquisition', 'merger', 'acquired', 'buyout', 'takeover',
  'ipo', 'public offering', 'goes public', 'listing', 'spac',
  'funding', 'series a', 'series b', 'series c', 'valuation', 'raised',
]

const ACQUISITION_KEYS = ['acquisition', 'merger', 'acquired', 'buyout', 'takeover']
const IPO_KEYS = ['ipo', 'public offering', 'goes public', 'listing', 'spac']
const FUNDING_KEYS = ['funding', 'series a', 'series b', 'series c', 'valuation', 'raised']

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function classifyDeal(text: string): { type: string; badge: string } {
  const lower = text.toLowerCase()
  if (ACQUISITION_KEYS.some(k => lower.includes(k))) return { type: 'acquisition', badge: 'badge-red' }
  if (IPO_KEYS.some(k => lower.includes(k))) return { type: 'ipo', badge: 'badge-purple' }
  if (FUNDING_KEYS.some(k => lower.includes(k))) return { type: 'funding', badge: 'badge-green' }
  return { type: 'deal', badge: 'badge-amber' }
}

export default function MandAPage() {
  const [items, setItems] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [startupsRes, newsRes] = await Promise.allSettled([
        api.get('/api/startups/feed', { params: { limit: 60 } }),
        api.get('/api/news/feed', { params: { limit: 60 } }),
      ])
      const startups = startupsRes.status === 'fulfilled'
        ? (startupsRes.value.data.articles || startupsRes.value.data.items || startupsRes.value.data || [])
        : []
      const news = newsRes.status === 'fulfilled'
        ? (newsRes.value.data.articles || newsRes.value.data.items || newsRes.value.data || [])
        : []

      const all = [...startups, ...news]
      const filtered = all.filter(a => {
        const text = `${a.title || ''} ${a.summary || ''} ${a.description || ''}`.toLowerCase()
        return MA_KEYWORDS.some(kw => text.includes(kw))
      })

      // Deduplicate by title
      const seen = new Set<string>()
      const unique = filtered.filter(a => {
        const key = (a.title || '').toLowerCase().trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })

      unique.sort((a, b) =>
        new Date(b.timestamp || b.published || 0).getTime() - new Date(a.timestamp || a.published || 0).getTime()
      )

      setItems(unique)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (!searchTerm) return true
    const text = `${item.title || ''} ${item.summary || ''} ${item.source || ''}`.toLowerCase()
    return text.includes(searchTerm.toLowerCase())
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitBranch size={24} className="text-amber" /> M&A &amp; IPO Tracker
          </h1>
          <p className="text-sm text-text-secondary mt-1">Acquisitions, mergers, IPOs, and funding rounds</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search deals..."
          className="input pl-9 py-2 text-xs"
        />
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Scanning for M&A activity...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => {
          const text = `${item.title || ''} ${item.summary || ''} ${item.description || ''}`
          const deal = classifyDeal(text)
          return (
            <div key={item.url || idx} className="card card-hover p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx(deal.badge, 'text-[10px]')}>{deal.type.toUpperCase()}</span>
                    <span className="text-[10px] text-text-muted">{item.source}</span>
                    <span className="text-[10px] text-text-muted">&middot;</span>
                    <span className="text-[10px] text-text-muted">{relativeTime(item.timestamp || item.published)}</span>
                  </div>
                  <h3 className="text-sm font-medium text-text-primary leading-snug">{item.title}</h3>
                  {(item.summary || item.description) && (
                    <p className="text-xs text-text-secondary mt-1 line-clamp-2">{item.summary || item.description}</p>
                  )}
                </div>
                {item.url && (
                  <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary flex-shrink-0">
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-sm text-text-muted">No M&A or IPO activity found matching your search.</p>
        </div>
      )}
    </div>
  )
}
